import * as XLSX from "xlsx";

/* ================= MATCH TESTS ================= */
export function getMatchingTests({
  testHistory,
  resetType,
  selector,
  customMode,
  from,
  to,
}) {
  if (!Array.isArray(testHistory)) return [];
  const matches = [];

  let normalCount = 0;
  let streamCount = 0;

  testHistory.forEach((test) => {
    // Type filter
    if (resetType !== "all" && test.testType !== resetType) return;

    // Per-type numbering
    let testNo;
    if (test.testType === "normal") {
      normalCount++;
      testNo = normalCount;
    } else {
      streamCount++;
      testNo = streamCount;
    }

    // Selector
    if (selector === "all") {
      matches.push(test);
      return;
    }

    if (selector === "custom") {
      if (!from || !to) return;

      // By Test #
      if (customMode === "id") {
        if (testNo >= Number(from) && testNo <= Number(to)) {
          matches.push(test);
        }
      }

      // By Date
      if (customMode === "date") {
        const d = test.date?.seconds
          ? test.date.seconds * 1000
          : new Date(test.date).getTime();

        if (
          d >= new Date(from).setHours(0, 0, 0, 0) &&
          d <= new Date(to).setHours(23, 59, 59, 999)
        ) {
          matches.push(test);
        }
      }
    }
  });

  return matches;
}

/* ================= EXPORT XLSX ================= */
export function exportFilteredHistory(params) {
  const tests = getMatchingTests(params);

  if (!tests.length) {
    alert("⚠ No matching tests to export.");
    return;
  }

  const rows = [];
  const headers = [
    "Test #",
    "Name",
    "Operation",
    "Difficulty",
    "Accuracy",
    "Correct",
    "Total",
    "Avg Time (s)",
    "Total Time (s)",
    "Test Date & Time",
  ];

  // Helper to push a table
  const pushTable = (title, list) => {
    rows.push({}); // empty row
    rows.push({ "Test #": title }); // title row
    rows.push({}); // empty row

    // Header row
    rows.push(
      headers.reduce((acc, h) => {
        acc[h] = h;
        return acc;
      }, {})
    );

    // Data rows
    list.forEach((t, i) => {
      rows.push({
        "Test #": i + 1,
        Name: t.name,
        Operation: t.operation,
        Difficulty: t.difficulty,
        Accuracy: `${t.accuracy}%`,
        Correct: t.correct,
        Total: t.total,
        "Avg Time (s)": t.avgTime,
        "Total Time (s)": t.timeTaken,
        "Test Date & Time": t.date?.seconds
          ? new Date(t.date.seconds * 1000).toLocaleString()
          : new Date(t.date).toLocaleString(),
      });
    });
  };

  // Split by type
  const normalTests = tests.filter((t) => t.testType === "normal");
  const streamTests = tests.filter((t) => t.testType === "stream");

  // Decide layout
  if (params.resetType === "all" && params.selector === "all") {
    if (normalTests.length) pushTable("NORMAL TEST HISTORY", normalTests);
    if (streamTests.length) pushTable("STREAM TEST HISTORY", streamTests);
  } else {
    const single = params.resetType === "stream" ? streamTests : normalTests;
    pushTable("TEST HISTORY", single);
  }

  // Create worksheet
  const worksheet = XLSX.utils.json_to_sheet(rows, { skipHeader: true });

  // 🔹 Center align + bold headers and titles
  const range = XLSX.utils.decode_range(worksheet["!ref"]);

  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = worksheet[addr];
      if (!cell) continue;

      cell.s = {
        alignment: {
          horizontal: "center",
          vertical: "center",
          wrapText: true,
        },
      };

      if (
        cell.v === "NORMAL TEST HISTORY" ||
        cell.v === "STREAM TEST HISTORY" ||
        cell.v === "TEST HISTORY" ||
        headers.includes(cell.v)
      ) {
        cell.s.font = { bold: true };
      }
    }
  }

  // 🔹 Auto column width
  worksheet["!cols"] = headers.map((h) => ({
    wch: Math.max(h.length, ...rows.map((r) => String(r[h] || "").length)) + 3,
  }));

  // Workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Test History");

  XLSX.writeFile(workbook, "test_history.xlsx");
}

/* ================= RESET ANALYSIS ================= */
export function resetAnalysisOnly() {
  localStorage.removeItem("analysisCache");
  alert("Analysis reset. History preserved.");
}
